/**
 * Shared paper-card shell for Aiko surfaces.
 *
 * Single source of truth — change here to retune the silhouette across every
 * Aiko helper at once (`<AikoTip>`, `<AikoTooltip>`, `<AikoHint>`,
 * `<AikoSurfaces>`).
 *
 * Tokens consumed (all carry safe defaults via the Tailwind preset's
 * `aikoTokensPlugin`; consumers override per-studio in their own `index.css`):
 *
 *   --aiko-paper    — bubble background (warm near-white in light, lifted
 *                     dark-blue in dark)
 *   --aiko-border   — hairline around the bubble
 *   --aiko-shadow   — depth shadow under the bubble (mode-aware)
 *   --radius        — corner rounding (consumer's brand radius)
 *
 * No inset highlight. The earlier shell painted a 1px white inset along the
 * top of the bubble; in dark mode against a coloured studio background it
 * read as a glow that fought the brand. The shell is now just paper + border
 * + soft depth.
 */
import { cn } from "./cn";

/** Canonical paper-card shell. Used by `<AikoTip>`, `<AikoTooltip>`, `<AikoHint>`. */
export const aikoShell = cn(
  "z-50",
  "bg-aiko-paper text-foreground",
  "rounded-xl",
  "border border-aiko-border",
  "shadow-aiko",
  "focus:outline-none",
  "animate-in fade-in-0 zoom-in-95",
  "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
  "motion-reduce:animate-none",
);

/**
 * Just the paper background + radius + motion — for `<AikoSurfaces>` variants
 * (`<AikoNudge>`, `<AikoIntervene>`) that need level-specific tinted borders
 * and heavier depth shadows. They drop the canonical `border` + `shadow-aiko`
 * and supply their own.
 */
export const aikoSurfaceBase = cn(
  "z-50",
  "bg-aiko-paper text-foreground",
  "rounded-xl",
  "focus:outline-none",
  "animate-in fade-in-0 zoom-in-95",
  "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
  "motion-reduce:animate-none",
);

/**
 * Tighter sibling of `aikoShell` for label-style tooltips. Same paper, border,
 * and brand surface, but smaller radius and a lighter shadow so it reads as a
 * tooltip rather than a card. Used by `<AikoTooltip>` when its tone heuristic
 * resolves to `minimal` (short string body, no title).
 */
export const aikoShellTight = cn(
  "z-50",
  "bg-aiko-paper text-foreground",
  "rounded-md",
  "border border-aiko-border",
  "shadow-sm",
  "focus:outline-none",
  "animate-in fade-in-0 zoom-in-95",
  "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
  "motion-reduce:animate-none",
);
